import { CarouselItemId } from '@homex/se-widget-flow-types';
import { useRef, useState } from 'react';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  addUploadedMediaFileRecord,
  removeUploadedMediaFileRecord,
  setCurrentCarouselItem,
} from 'actions';
import phoneScreen from 'assets/phone-screen.png';
import { AndroidCameraContextBubble } from 'components/AndroidCameraContextBubble';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { CarouselItemMediaUploadBox } from 'components/CarouselItemMediaUploadBox';
import { Phrase } from 'components/Phrase';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  IState,
  MediaUploadCarouselItem,
  Platform,
} from 'typings';
import { isNativeCameraSupported } from 'utils/cordovaApi';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  AddSvgStyled,
  BoxContainer,
  ButtonsDesktop,
  ButtonsMobile,
  CellphoneImage,
  CircleButtonStyled,
  DesktopHeader,
  DropzoneDivStyled,
  FormGroup,
  LowerHeader,
  SkipButtonMobile,
  TextLower,
  TextLowerMobile,
  TextUpperDesktop,
  TextUpperMobile,
  UploadBoxContainer,
  UploadContainer,
} from './styles';

export const CAROUSEL_ITEM_ID = CarouselItemId.MediaUpload;

export const CarouselItemMediaUploadBottom = () => {
  const dispatch = useThunkDispatch();
  const { flow, platform, uploadedMediaFileRecords } = useSelector(
    (state: IState) => ({
      flow: state.client.flow,
      platform: state.client.platform,
      uploadedMediaFileRecords: state.userInput.uploadedMediaFileRecords,
    }),
  );

  const [isAndroidContextBubbleVisible, setIsAndroidContextBubbleVisible] =
    useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [clickedUploadBoxIndex, setClickedUploadBoxIndex] = useState<
    number | null
  >(null);
  const dropzoneRef = useRef<DropzoneRef | null>(null);
  const { buttonType, repairCommentsEnabled } = useTheme();
  const MAX_FILES = 4;

  const flowCarouselItem = tryGetFlowCarouselItem<MediaUploadCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  const handleFileRemove = (index: number) => {
    dispatch(removeUploadedMediaFileRecord(index));
  };

  const handleUploadClickable = (index: number | null) => {
    if (typeof index === 'number') {
      setClickedUploadBoxIndex(index);
    }

    if (platform === Platform.ANDROID && isNativeCameraSupported()) {
      setIsAndroidContextBubbleVisible(true);
    } else if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const handleDropAccepted = (droppedFiles: Array<File>) => {
    const emptyIndex = uploadedMediaFileRecords.indexOf(null);

    // If the user clicked a box, we remember the index they clicked and use it to add the media to the correct box.
    // Otherwise, they either dragged media on or clicked the camera button on mobile, so we fill those in to the
    // empty boxes in the order they were received
    if (clickedUploadBoxIndex) {
      const [droppedFile] = droppedFiles;
      dispatch(
        addUploadedMediaFileRecord(droppedFile ?? null, clickedUploadBoxIndex),
      );
    } else {
      while (emptyIndex !== -1 && droppedFiles.length > 0) {
        const nextDroppedFile = droppedFiles.shift();

        if (nextDroppedFile) {
          dispatch(addUploadedMediaFileRecord(nextDroppedFile, emptyIndex));
        }
      }
    }

    setIsAndroidContextBubbleVisible(false);
    setIsDraggingOver(false);
    setClickedUploadBoxIndex(null);

    return false;
  };

  const handleDropRejected = () => {
    setIsAndroidContextBubbleVisible(false);
    setClickedUploadBoxIndex(null);
  };

  const handleDragEnter = () => {
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleContinueClick = () => {
    let nextCarouselItemId;

    if (flowCarouselItem) {
      nextCarouselItemId = flowCarouselItem.options.destinations.next;
    } else {
      nextCarouselItemId = repairCommentsEnabled
        ? CarouselItemId.RepairComments
        : CarouselItemId.CustomerLookup;
    }

    nextCarouselItemId &&
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          nextCarouselItemId,
        ),
      );
  };

  const hasFiles = !!uploadedMediaFileRecords.find((file) => file !== null);
  const continueButtonPhraseId = hasFiles
    ? 'MEDIA_UPLOAD.CONTINUE.BUTTON'
    : 'MEDIA_UPLOAD.SKIP.BUTTON';
  const textUpperPhraseId = hasFiles
    ? 'MEDIA_UPLOAD.DESCRIPTION.FILES'
    : 'MEDIA_UPLOAD.DESCRIPTION.NO_FILES.HTML';

  return (
    <FormGroup>
      <DesktopHeader>
        <CellphoneImage alt="" height="116" src={phoneScreen} width="241" />
        <LowerHeader>
          <Phrase id="MEDIA_UPLOAD.HEADER_LOWER" />
        </LowerHeader>
      </DesktopHeader>
      <UploadContainer isDraggingOver={isDraggingOver}>
        <Dropzone
          accept="image/*,video/*"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDropAccepted={handleDropAccepted}
          onDropRejected={handleDropRejected}
          ref={(dropzone) => (dropzoneRef.current = dropzone)}
        >
          {({ getInputProps, getRootProps }) => (
            <DropzoneDivStyled {...getRootProps()}>
              <input {...getInputProps()} />
            </DropzoneDivStyled>
          )}
        </Dropzone>
        <BoxContainer isDisabled={isDraggingOver}>
          <TextUpperDesktop hasFiles={hasFiles}>
            <Phrase id={textUpperPhraseId} />
          </TextUpperDesktop>
          <TextUpperMobile>
            <Phrase id="MEDIA_UPLOAD.DESCRIPTION.NO_FILES.HTML" />
          </TextUpperMobile>
          <UploadBoxContainer>
            {[...Array(MAX_FILES)].map((_, index) => (
              <CarouselItemMediaUploadBox
                file={uploadedMediaFileRecords[index] ?? null}
                key={index}
                mediaIdIndex={index}
                onCancel={handleFileRemove.bind(this, index)}
                onNoFileClick={handleUploadClickable.bind(this, index)}
              />
            ))}
          </UploadBoxContainer>
          <TextLower>
            <Phrase id="MEDIA_UPLOAD.LOWER_TEXT" />
          </TextLower>
        </BoxContainer>
        <AndroidCameraContextBubble
          isVisible={isAndroidContextBubbleVisible}
          onFilesReceived={handleDropAccepted}
        />
      </UploadContainer>
      <ButtonsMobile>
        {hasFiles ? (
          <>
            <TextLowerMobile>
              <Phrase id="MEDIA_UPLOAD.MOBILE.DESCRIPTION.LOWER.FILES" />
            </TextLowerMobile>
            <CarouselContinueButton
              isDisabled={false}
              label="Continue"
              onClick={handleContinueClick}
            />
          </>
        ) : (
          <>
            <SkipButtonMobile onClick={handleContinueClick}>
              <Phrase id="MEDIA_UPLOAD.MOBILE.SKIP.BUTTON" />
            </SkipButtonMobile>
            <CircleButtonStyled
              icon={<AddSvgStyled />}
              label={Phrase({ id: 'MEDIA_UPLOAD.MOBILE.ADD.BUTTON' })}
              onClick={handleUploadClickable.bind(this, null)}
              size="large"
            />
          </>
        )}
      </ButtonsMobile>
      <ButtonsDesktop>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          continueButtonLabel={Phrase({ id: continueButtonPhraseId })}
          onContinueClick={handleContinueClick}
        />
      </ButtonsDesktop>
    </FormGroup>
  );
};
